/**
 * Transaction Detail Modal
 * ========================
 * Full transaction details with reclassification functionality.
 *
 * Features:
 * - View complete transaction information
 * - Reclassify to any of the 48 BI categories
 * - Mark as reviewed or flagged
 * - View source document in PDF viewer
 * - Complete audit trail of reclassifications
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, FileText, AlertCircle, CheckCircle, History, RefreshCw } from 'lucide-react';
import CpaWorkpaperTextarea from '../privilege/CpaWorkpaperTextarea';
import api from '../../utils/api';
import PDFViewerModal from './PDFViewerModal';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { FinancialValue } from '../shared/FinancialValue';

interface Transaction {
  id: string;
  date: string;
  vendor: string;
  description: string;
  amount: number;
  source_file: string;
  source_page: number;
  category: string;
  subcategory?: string;
  original_category?: string;
  is_reclassified: boolean;
  reclassification_reason?: string;
  checked: boolean;
  flagged: boolean;
  flag_reason?: string;
  needs_review: boolean;
}

interface TransactionDetailModalProps {
  transaction: Transaction;
  reportId: string;
  onClose: () => void;
  onUpdate: () => void;
}

interface ReclassificationHistory {
  timestamp: string;
  user: string;
  from_category: string;
  to_category: string;
  reason: string;
}

// All 48 BI Categories
const BI_CATEGORIES = [
  "Food & Beverage Inventory",
  "Salaries & Wages",
  "Rent",
  "Utilities",
  "Business Insurance",
  "Advertising",
  "Marketing",
  "Depreciation Expense",
  "Other Expenses",
  "Cost of Goods Sold",
  "Personnel Expenses",
  "Occupancy Expenses",
  "Insurance Expenses",
  "Professional Services",
  "Technology & Software",
  "Office & Supplies",
  "Vehicle & Transportation",
  "Bank & Financial Fees",
  "Taxes & Licenses",
  "Debt Service",
  "Transfer",
  "Sales Revenue",
  "Travel & Entertainment",
  "Inventory & Merchandise",
  "Miscellaneous",
  "Commissions",
  "Contract Labor",
  "Freight & Shipping",
  "Repairs & Maintenance",
  "Cleaning & Janitorial",
  "Security Services",
  "Training & Education",
  "Dues & Subscriptions",
  "Equipment Rental",
  "Storage Expense",
  "Bad Debt Expense",
  "Telephone & Communications",
  "Uniforms",
  "Donations & Contributions",
  "Franchise Fees",
  "Drug Testing",
  "Officer Compensation",
  "Retirement Expense",
  "Program Expenses",
  "Housing Allowance",
  "Printing & Reproduction",
  "Teaching Materials",
  "Childcare"
];

export default function TransactionDetailModal({
  transaction,
  reportId,
  onClose,
  onUpdate
}: TransactionDetailModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, true);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const [showReclassifyForm, setShowReclassifyForm] = useState(false);
  const [newCategory, setNewCategory] = useState(transaction.category);
  const [newSubcategory, setNewSubcategory] = useState(transaction.subcategory || '');
  const [reclassifyReason, setReclassifyReason] = useState('');
  const [history, setHistory] = useState<ReclassificationHistory[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Local state for checked/flagged
  const [isChecked, setIsChecked] = useState(transaction.checked);
  const [isFlagged, setIsFlagged] = useState(transaction.flagged);

  // PDF Viewer state
  const [showPdfViewer, setShowPdfViewer] = useState(false);

  useEffect(() => {
    if (transaction.is_reclassified) {
      fetchReclassificationHistory();
    }
  }, [transaction.id]);

  const fetchReclassificationHistory = async () => {
    try {
      const data = await api.get(`/api/v1/reconciliation/history/${reportId}?transaction_id=${transaction.id}`);
      setHistory(data.history || []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  };

  const handleReclassify = async () => {
    if (!reclassifyReason.trim() || reclassifyReason.trim().length < 10) {
      setError('Please provide a reason for reclassification (minimum 10 characters)');
      return;
    }

    if (newCategory === transaction.category && newSubcategory === transaction.subcategory) {
      setError('Please select a different category');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post(`/api/v1/reconciliation/transactions/${reportId}/${transaction.id}/reclassify`, {
        new_category: newCategory,
        new_subcategory: newSubcategory || null,
        reason: reclassifyReason
      });

      setShowReclassifyForm(false);
      setReclassifyReason('');
      onUpdate();
    } catch (err: any) {
      console.error('Reclassification failed:', err);
      setError(err.response?.data?.detail || 'Failed to reclassify transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleChecked = async () => {
    try {
      await api.patch(`/api/v1/reconciliation/transactions/${reportId}/${transaction.id}`, {
        checked: !isChecked
      });
      setIsChecked(!isChecked);
      onUpdate();
    } catch (err) {
      console.error('Failed to toggle checked:', err);
    }
  };

  const handleToggleFlagged = async () => {
    try {
      await api.patch(`/api/v1/reconciliation/transactions/${reportId}/${transaction.id}`, {
        flagged: !isFlagged
      });
      setIsFlagged(!isFlagged);
      onUpdate();
    } catch (err) {
      console.error('Failed to toggle flagged:', err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label="Transaction Detail" className="rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b sticky top-0 z-10" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }}>
          <h2 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Transaction Detail</h2>
          <button
            onClick={onClose}
            className="transition-colors"
            style={{ color: 'var(--color-text-dim)' }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-text-label)' }}>Date</label>
              <p className="text-sm mt-1" style={{ color: 'var(--color-text-primary)' }}>
                {new Date(transaction.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-text-label)' }}>Amount</label>
              <p className="text-lg font-bold mt-1" style={{ color: 'var(--color-text-primary)' }}>
                <FinancialValue value={transaction.amount} cents />
              </p>
            </div>
          </div>

          {/* Vendor & Description */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-text-label)' }}>Vendor</label>
            <p className="text-sm mt-1 font-medium" style={{ color: 'var(--color-text-primary)' }}>{transaction.vendor || transaction.description || '—'}</p>
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-text-label)' }}>Description</label>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>{transaction.description || transaction.vendor || '—'}</p>
          </div>

          {/* Source Document */}
          <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
            <div className="flex items-center justify-between">
              <div>
                <label className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-needs-human-text)' }}>Source Document</label>
                <p className="text-sm mt-1" style={{ color: 'var(--color-needs-human-text)' }}>{transaction.source_file}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--color-needs-human-text)' }}>Page {transaction.source_page}</p>
              </div>
              <button
                onClick={() => setShowPdfViewer(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg"
                style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                <FileText size={16} />
                View PDF
              </button>
            </div>
          </div>

          {/* Current Classification */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-text-label)' }}>Current Classification</label>
            <div className="mt-2 space-y-2">
              <div className="flex items-center gap-2">
                <span
                  className="inline-block px-3 py-1.5 text-sm rounded-full font-medium"
                  style={
                    transaction.is_reclassified
                      ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', border: '1px solid var(--color-needs-human-tint-bd)' }
                      : { backgroundColor: 'transparent', color: 'var(--color-verified)' }
                  }
                >
                  {transaction.category}
                  {transaction.subcategory && ` → ${transaction.subcategory}`}
                </span>
                {transaction.is_reclassified && (
                  <span className="text-xs font-medium" style={{ color: 'var(--color-needs-human-text)' }}>RECLASSIFIED</span>
                )}
              </div>

              {transaction.is_reclassified && transaction.original_category && (
                <div className="text-xs rounded px-3 py-2" style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-elevated)' }}>
                  <strong>Originally:</strong> {transaction.original_category}
                  {transaction.reclassification_reason && (
                    <div className="mt-1 italic">
                      <strong>Reason:</strong> {transaction.reclassification_reason}
                    </div>
                  )}
                </div>
              )}

              {/* Show history button if reclassified */}
              {transaction.is_reclassified && (
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="flex items-center gap-2 text-sm font-medium"
                  style={{ color: 'var(--color-needs-human-text)' }}
                >
                  <History size={16} />
                  {showHistory ? 'Hide' : 'View'} Reclassification History
                </button>
              )}

              {/* History Display */}
              {showHistory && history.length > 0 && (
                <div className="border rounded-lg p-4 space-y-2 max-h-40 overflow-y-auto" style={{ borderColor: 'var(--color-border)' }}>
                  {history.map((entry, idx) => (
                    <div key={idx} className="text-xs border-b pb-2 last:border-0" style={{ borderColor: 'var(--color-border-faint)' }}>
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{entry.user || 'Unknown'}</span>
                        <span style={{ color: 'var(--color-text-muted)' }}>
                          {entry.timestamp ? new Date(entry.timestamp).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          }) : '—'}
                        </span>
                      </div>
                      <div style={{ color: 'var(--color-text-secondary)' }}>
                        <span className="line-through">{entry.from_category || '—'}</span> → <span className="font-medium">{entry.to_category || '—'}</span>
                      </div>
                      {entry.reason && <div className="italic mt-1" style={{ color: 'var(--color-text-secondary)' }}>{entry.reason}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Flags */}
          {transaction.flagged && transaction.flag_reason && (
            <div className="border rounded-lg p-4 flex items-start gap-3" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
              <AlertCircle className="flex-shrink-0" size={20} style={{ color: 'var(--color-brand)' }} />
              <div>
                <label className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-needs-human-text)' }}>Flag Reason</label>
                <p className="text-sm mt-1" style={{ color: 'var(--color-needs-human-text)' }}>{transaction.flag_reason}</p>
              </div>
            </div>
          )}

          {transaction.needs_review && (
            <div className="border rounded-lg p-3 flex items-center gap-2" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
              <AlertCircle size={16} style={{ color: 'var(--color-brand)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--color-needs-human-text)' }}>This transaction needs review</span>
            </div>
          )}

          {/* Review Status Checkboxes */}
          <div className="flex items-center gap-6 pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={handleToggleChecked}
                className="rounded"
                style={{ borderColor: 'var(--color-border)', accentColor: 'var(--color-verified)' }}
              />
              <CheckCircle size={16} style={{ color: isChecked ? 'var(--color-verified)' : 'var(--color-text-dim)' }} />
              <span className={isChecked ? 'font-medium' : ''} style={{ color: isChecked ? 'var(--color-verified)' : 'var(--color-text-secondary)' }}>Reviewed</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={isFlagged}
                onChange={handleToggleFlagged}
                className="rounded"
                style={{ borderColor: 'var(--color-border)', accentColor: 'var(--color-brand)' }}
              />
              <AlertCircle size={16} style={{ color: isFlagged ? 'var(--color-brand)' : 'var(--color-text-dim)' }} />
              <span className={isFlagged ? 'font-medium' : ''} style={{ color: isFlagged ? 'var(--color-needs-human-text)' : 'var(--color-text-secondary)' }}>Flagged</span>
            </label>
          </div>

          {/* Reclassification Form */}
          {!showReclassifyForm ? (
            <button
              onClick={() => setShowReclassifyForm(true)}
              className="w-full px-4 py-3 text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            >
              <RefreshCw size={14} /> Reclassify Transaction
            </button>
          ) : (
            <div className="border rounded-lg p-5 space-y-4" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold uppercase tracking-wide" style={{ color: 'var(--color-text-primary)' }}>Reclassification Form</h3>
                <button
                  onClick={() => {
                    setShowReclassifyForm(false);
                    setError('');
                    setNewCategory(transaction.category);
                    setNewSubcategory(transaction.subcategory || '');
                    setReclassifyReason('');
                  }}
                  style={{ color: 'var(--color-text-dim)' }}
                >
                  <X size={18} />
                </button>
              </div>

              {error && (
                <div className="border rounded p-3 text-sm" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>
                  {error}
                </div>
              )}

              {/* Category Dropdown */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  New Category <span style={{ color: 'var(--color-needs-human-text)' }}>*</span>
                </label>
                <select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
                  style={{ borderColor: 'var(--color-border)' }}
                >
                  {BI_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Subcategory (optional) */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                  Subcategory (Optional)
                </label>
                <input
                  type="text"
                  value={newSubcategory}
                  onChange={(e) => setNewSubcategory(e.target.value)}
                  placeholder="e.g., Fixed, Variable, Specific detail..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent"
                  style={{ borderColor: 'var(--color-border)' }}
                />
              </div>

              {/* Reason (required) — wrapped per Phase 10 Task 10.5 */}
              <CpaWorkpaperTextarea
                id="reclassify-reason"
                label={
                  <span className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>
                    Reason for Reclassification <span style={{ color: 'var(--color-needs-human-text)' }}>*</span>
                  </span>
                }
                value={reclassifyReason}
                onChange={(e) => setReclassifyReason(e.target.value)}
                placeholder="Explain why this transaction is being reclassified (minimum 10 characters)..."
                className="w-full h-24 px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent resize-none"
                style={{ borderColor: 'var(--color-border)' }}
                data-testid="transaction-detail-reason"
                helperText={`${reclassifyReason.length}/10 characters minimum`}
              />

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowReclassifyForm(false);
                    setError('');
                    setNewCategory(transaction.category);
                    setNewSubcategory(transaction.subcategory || '');
                    setReclassifyReason('');
                  }}
                  disabled={loading}
                  className="flex-1 px-4 py-2 text-sm font-medium border rounded-lg disabled:opacity-50"
                  style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleReclassify}
                  disabled={loading || reclassifyReason.length < 10}
                  className="flex-1 px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ color: 'var(--color-on-brand)', backgroundColor: 'var(--color-brand)' }}
                >
                  {loading ? 'Reclassifying...' : '✓ Confirm Reclassification'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t sticky bottom-0" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }}>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
            style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-elevated)' }}
          >
            Close
          </button>
        </div>
      </div>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={showPdfViewer}
        reportId={reportId}
        sourceFile={transaction.source_file}
        sourcePage={transaction.source_page}
        transactionContext={{
          date: transaction.date,
          vendor: transaction.vendor,
          amount: transaction.amount
        }}
        onClose={() => setShowPdfViewer(false)}
      />
    </div>
  );
}
